/*
[GNU Affero General Public License (AGPL)](https://www.gnu.org/licenses/agpl-3.0.html)

VeloceUI - A UI component library for Angular v17+ made with care by Colibryx Srl in Italy.
Copyright (C) 2025 Colibryx S.r.l.

This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as published by
the Free Software Foundation, either version 3 of the License, or
(at your option) any later version.

This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
GNU Affero General Public License for more details.

You should have received a copy of the GNU Affero General Public License
along with this program. If not, see <https://www.gnu.org/licenses/>.

This license applies to any accompanying files located directly in the same directory.
*/

import {
	type CdkVirtualScrollViewport,
	ScrollingModule,
} from "@angular/cdk/scrolling";
import { CommonModule } from "@angular/common";
import {
	ChangeDetectorRef,
	Component,
	type ElementRef,
	EventEmitter,
	Output,
	TemplateRef,
	computed,
	contentChild,
	effect,
	forwardRef,
	inject,
	input,
	signal,
	viewChild,
} from "@angular/core";
import { toObservable } from "@angular/core/rxjs-interop";
import {
	type ControlValueAccessor,
	FormsModule,
	NG_VALUE_ACCESSOR,
} from "@angular/forms";
import type {
	FiltersObject,
	ItemsService,
} from "@app/@classes/abstract/items-service-interface";
import type { TablesOrViews, TablesOrViewsKeys } from "@app/@classes/supabase";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import Fuse from "fuse.js";
import {
	type Observable,
	Subject,
	debounceTime,
	map,
	of,
	pairwise,
	take,
	takeUntil,
	tap,
	timer,
} from "rxjs";
import { CheckboxComponent } from "../checkbox/checkbox/checkbox.component";
import { ChipComponent } from "../chip/chip.component";
import { PopoverDirective } from "../floating/popover/popover.directive";
import { PopoverModule } from "../floating/popover/popover.module";
import { TooltipModule } from "../floating/tooltip/tooltip.module";
import { IconComponent } from "../icon/icon.component";
import { InputNumberComponent } from "../input-number/input-number.component";
import { InputComponent } from "../input/input.component";
import { LoaderModule } from "../loader/loader.module";
import { SelectAsyncLoaderService } from "./select-async-loader.service";

export interface CbSelectOption {
	value: unknown;
	label: string;
	item?: unknown;
	group?: string;
	isDisabled?: boolean;
}

export type CbAsyncSelect<T extends TablesOrViews<TablesOrViewsKeys>> = {
	service: ItemsService<T>;
	valueKey: keyof T;
	filters?: FiltersObject<T>;
	limit?: number;
	/** Array of field names to search on using ilike operator. If not provided, falls back to labelKey */
	searchFields?: string[];
} & ({ labelKey: keyof T } | { labelCallback: (item: T) => string });

export type CbAsyncGroupedSelect<T extends TablesOrViews<TablesOrViewsKeys>> = {
	service: ItemsService<T>;
	valueKey: keyof T;
	groupBy: keyof T;
	filters?: FiltersObject<T>;
	/** Array of field names to search on using ilike operator. If not provided, falls back to labelKey */
	searchFields?: string[];
} & ({ labelKey: keyof T } | { labelCallback: (item: T) => string }) &
	({ groupLabelKey: keyof T } | { groupLabelCallback: (item: T) => string });

export interface CbGroupedSelectOption {
	label?: string;
	options: CbSelectOption[];
}

const noop = () => { };

const CUSTOM_INPUT_CONTROL_VALUE_ACCESSOR = {
	provide: NG_VALUE_ACCESSOR,
	useExisting: forwardRef(() => SelectComponent),
	multi: true,
};

@Component({
	selector: "cb-select",
	imports: [
		CommonModule,
		FormsModule,
		IconComponent,
		TooltipModule,
		PopoverModule,
		InputComponent,
		FormsModule,
		TranslateModule,
		CheckboxComponent,
		LoaderModule,
		ScrollingModule,
		ChipComponent,
		InputNumberComponent
	],
	templateUrl: "./select.component.html",
	styleUrl: "./select.component.scss",
	providers: [CUSTOM_INPUT_CONTROL_VALUE_ACCESSOR],
})
export class SelectComponent implements ControlValueAccessor {
	private readonly translateService: TranslateService =
		inject(TranslateService);
	private readonly cdr: ChangeDetectorRef = inject(ChangeDetectorRef);
	private readonly asyncLoaderService: SelectAsyncLoaderService = inject(SelectAsyncLoaderService);

	/**
	 * The options to display in the select.
	 */
	public readonly options = input<CbSelectOption[]>([]);

	/**
	 * The grouped options to display in the select.
	 */
	public readonly groupedOptions = input<CbGroupedSelectOption[]>([]);

	/**
	 * The options to disable in the select.
	 */
	public readonly disabledOptions = input<unknown[]>([]);

	/**
	 * The disable check callback to disable options.
	 */
	public readonly disableCheckCallback =
		input<(option: CbSelectOption) => boolean>();

	/**
	 * If the filtering is enabled
	 */
	public readonly filter = input<boolean>();

	/**
	 * If the translate is enabled
	 */
	public readonly translateGroupLabels = input<boolean>();

	/**
	 * If the translate is enabled
	 */
	public readonly translateOptions = input<boolean>();

	/**
	 * The async options to display in the select.
	 */
	public readonly async = input<CbAsyncSelect<any>>();

	/**
	 * The async grouped options to display in the select.
	 */
	public readonly groupedAsync = input<CbAsyncGroupedSelect<any>>();

	/**
	 * Async additional dynamic filters
	 */
	public readonly asyncFilters = input<FiltersObject<any>>();

	/**
	 * Placeholder of the input
	 */
	public readonly placeholder = input<string>();

	/**
	 * If the virtual scroll is enabled
	 */
	public readonly virtualScrollEnabled = input<boolean>(false);

	/**
	 * The height of the options for the virtual scroll
	 */
	public readonly optionsHeight = input<string>("30px");

	/**
	 * The height of the options as a number for virtual scroll
	 */
	public readonly optionsHeightNumber = computed(() => {
		return Number.parseFloat(this.optionsHeight());
	});

	/**
	 * Label of the input
	 */
	public readonly label = input<string>();

	/**
	 * Hint of the input
	 */
	public readonly hint = input<string>();

	/**
	 * Type of the search
	 */
	public readonly searchType = input<"string" | "number">("string");

	/**
	 * Info of the input displayed with a question mark
	 * when this is defined as trailing icon
	 */
	public readonly info = input<string>();

	/**
	 * If the input is multiline
	 */
	public readonly multiline = input<boolean>();

	/**
	 * If the input is required
	 */
	public readonly required = input<boolean>();

	/**
	 * If the input is disabled
	 */
	public readonly disabled = input<boolean>();

	/**
	 * Prefix symbol of the input
	 */
	public readonly prefix = input<string>();

	/**
	 * Suffix symbol of the input
	 */
	public readonly suffix = input<string>();

	/**
	 * Leading text of the input (ex. http://)
	 */
	public readonly leadingText = input<string>();

	/**
	 * Trailing text of the input (ex. .com)
	 */
	public readonly trailingText = input<string>();

	/**
	 * Leading icon of the input
	 */
	public readonly icon = input<string>();

	/**
	 * Whether to show the reset button
	 */
	public readonly resetButton = input<boolean | undefined>(false);

	/**
	 * Whether the input has an error
	 */
	public readonly error = input<boolean>();

	/**
	 * The selection mode of the select
	 */
	public readonly selectionMode = input<"single" | "multiple">("single");

	/**
	 * If the select allows the user to insert free text
	 */
	public readonly enableFreeText = input(false);

	/**
	 * If the select is forced to have a value
	 */
	public readonly forceValue = input(false);

	/**
	 * If the select width should match the parent width
	 */
	public readonly matchParentWidth = input(true);

	/**
	 * Reference to the prefix element
	 */
	readonly prefixElement = viewChild<ElementRef>("Prefix");

	/**
	 * Reference to the input element
	 */
	readonly inputElement = viewChild.required<ElementRef>("Input");

	/**
	 * Reference to the suffix element
	 */
	readonly suffixElement = viewChild<ElementRef>("Suffix");

	/**
	 * Reference to the popover element
	 */
	readonly popover = viewChild(PopoverDirective);

	/**
	 * Reference to the dropdown container element
	 */
	readonly dropdownContainer = viewChild<ElementRef>("DropdownContainer");

	/**
	 * Reference to the virtual scroll viewport
	 */
	readonly virtualScrollViewport = viewChild<CdkVirtualScrollViewport>(
		"VirtualScrollViewport",
	);

	/**
	 * Emits the value of the input on change
	 */
	@Output() public change: EventEmitter<unknown> = new EventEmitter<unknown>();

	/**
	 * Emits the complete selected option(s) on change - includes value, label, and item
	 */
	@Output() public selectedOptionChange: EventEmitter<CbSelectOption | undefined> = new EventEmitter<CbSelectOption | undefined>();

	@Output() public selectedOptionsListChange: EventEmitter<CbSelectOption[] | undefined> = new EventEmitter<CbSelectOption[] | undefined>();

	public readonly optionTemplate = contentChild("SelectOptionTemplate", {
		read: TemplateRef,
	});
	public readonly valueTemplate = contentChild("SelectValueTemplate", {
		read: TemplateRef,
	});

	private readonly _externalOptionTemplate = signal<
		TemplateRef<any> | undefined
	>(undefined);
	private readonly _externalValueTemplate = signal<
		TemplateRef<any> | undefined
	>(undefined);

	private readonly isLoadingFirstOption = signal(false);
	public readonly focusedOptionIndex = signal(-1);
	public readonly isLoading = signal(false);
	public readonly searchText = signal<string | null>(null);
	public readonly searchNumber = signal<number | null>(null);

	public readonly filteredOptions = signal<CbSelectOption[]>([]);
	public readonly filteredGroupedOptions = signal<CbGroupedSelectOption[]>([]);

	private destroy$ = new Subject<void>();

	/**
	 * The internal data model
	 */
	public innerValue = signal<CbSelectOption[]>([]);

	private readonly isPopoverOpened = signal(false);

	private values = signal<unknown[]>([]);

	protected applyFilters$ = new Subject<void>();

	constructor() {
		effect(() => {
			this.setInnerValue();
		});

		effect(() => {
			this.checkDisabledOptions();
		});

		// effect(() => {
		// 	if (this.selectionMode() === "single") {
		// 		this.onChangeCallback(this.selectedValues()[0]);
		// 		this.change.emit(this.selectedValues()[0]);
		// 	} else {
		// 		this.onChangeCallback(this.selectedValues());
		// 		this.change.emit(this.selectedValues());
		// 	}
		// });

		this.selectedValues$
			.pipe(
				takeUntil(this.destroy$),
				pairwise(),
				tap(([oldVal, newVal]) => {
					// Compare arrays by their values
					const areArraysEqual =
						Array.isArray(oldVal) &&
						Array.isArray(newVal) &&
						oldVal.length === newVal.length &&
						oldVal.every((val, index) => val === newVal[index]);

					if (!areArraysEqual) {
						if (this.selectionMode() === "single") {
							// Emit value for backward compatibility (ControlValueAccessor)
							this.onChangeCallback(this.selectedValues()[0]);
							this.change.emit(this.selectedValues()[0]);

							// Emit complete option for enhanced functionality
							this.selectedOptionChange.emit(this.innerValue()[0]);
						} else {
							// Emit values for backward compatibility (ControlValueAccessor)
							this.onChangeCallback(this.selectedValues());
							this.change.emit(this.selectedValues());

							// Emit complete options for enhanced functionality
							this.selectedOptionsListChange.emit(this.innerValue());
						}
					}
				}),
			)
			.subscribe();

		toObservable(this.options)
			.pipe(
				takeUntil(this.destroy$),
				tap(() => this.applyFilters$.next()),
			)
			.subscribe();

		toObservable(this.groupedOptions)
			.pipe(
				takeUntil(this.destroy$),
				tap(() => this.applyFilters$.next()),
			)
			.subscribe();

		toObservable(this.async)
			.pipe(
				takeUntil(this.destroy$),
				tap(() => this.applyFilters$.next()),
			)
			.subscribe();

		toObservable(this.groupedAsync)
			.pipe(
				takeUntil(this.destroy$),
				tap(() => this.applyFilters$.next()),
			)
			.subscribe();

		toObservable(this.searchText)
			.pipe(
				takeUntil(this.destroy$), // Cleanup on component destroy
				debounceTime(300), // Wait for 300ms of silence on input
				tap(() => {
					// Switch to loading observable, but only emit when loading is false
					this.applyFilters$.next();
				}),
			)
			.subscribe();

		toObservable(this.searchNumber)
			.pipe(
				takeUntil(this.destroy$),
				debounceTime(300),
				tap(() => this.applyFilters$.next()),
			)
			.subscribe();

		this.applyFilters$
			.pipe(
				takeUntil(this.destroy$),
				debounceTime(50),
				tap(() => this.applyFilter()),
			)
			.subscribe();
	}

	ngOnInit() {
		// Apply the padding to the input element
		// after the view is rendered
		// based on the prefix and suffix elements
		requestAnimationFrame(() => {
			let leftPadding = "0.5rem";
			const rightPadding = "0.5rem";

			const prefixElement = this.prefixElement();
			if (prefixElement) {
				leftPadding = `${prefixElement.nativeElement.offsetWidth}px`;
			}

			// const suffixElement = this.suffixElement();
			// if (suffixElement) {
			// 	rightPadding = `${suffixElement.nativeElement.offsetWidth}px`;
			// }

			const inputElement = this.inputElement();
			if (inputElement) {
				inputElement.nativeElement.style.paddingLeft = leftPadding;
				inputElement.nativeElement.style.paddingRight = rightPadding;
			}
		});
	}

	public readonly canReset = computed(() => {
		return this.resetButton() === true && this.innerValue().length > 0;
	});

	public readonly currentFlattenedOptions = computed(() => {
		if (this.groupedOptions()?.length > 0) {
			return this.groupedOptions().flatMap((group) => group.options);
		}

		return this.options();
	});

	public readonly currentFilteredFlattenedOptions = computed(() => {
		if (this.filteredGroupedOptions().length > 0) {
			return this.filteredGroupedOptions().flatMap((group) => group.options);
		}

		return this.filteredOptions();
	});

	public readonly strValue = computed(() => {
		return this.innerValue()
			.map((o) => {
				return this.translateOptions()
					? this.translateService.instant(o.label)
					: o.label;
			})
			.join(", ");
	});

	public readonly selectedValues = computed(() => {
		return this.innerValue().map((o) => o.value);
	});

	public readonly selectedValues$ = toObservable(this.selectedValues);

	public readonly showFreeTextInsert = computed(() => {
		return (
			this.enableFreeText() &&
			this.filteredOptions().find((o) => o.value === this.searchText()) ===
			undefined &&
			this.innerValue().find((o) => o.value === this.searchText()) === undefined
		);
	});

	/**
	 * Returns the value of the input
	 */
	/* public get value(): CbSelectOption[] {
		return this.innerValue;
	} */

	/**
	 * Sets the value of the input
	 */
	/* public set value(v: CbSelectOption[]) {
		if (v !== this.innerValue) {
			this.innerValue = v;

			if (this.selectionMode() === "single") {
				this.onChangeCallback(this.selectedValues()[0]);
				this.change.emit(this.selectedValues()[0]);
			} else {
				this.onChangeCallback(this.selectedValues);
				this.change.emit(this.selectedValues);
			}
		}
	} */

	public setOptionTemplate(template: TemplateRef<any> | undefined): void {
		this._externalOptionTemplate.set(template);
	}

	public setValueTemplate(template: TemplateRef<any> | undefined): void {
		this._externalValueTemplate.set(template);
	}

	public readonly shownOptionTemplate = computed(() => {
		return this._externalOptionTemplate() || this.optionTemplate();
	});

	public readonly shownValueTemplate = computed(() => {
		return this._externalValueTemplate() || this.valueTemplate();
	});

	protected applyFilter(): void {
		if (!this.isPopoverOpened()) return;
		if (this.isLoading()) return;

		const async = this.async();
		const groupedAsync = this.groupedAsync();
		const searchText = this.searchText();
		const searchNumber = this.searchNumber();

		if (async) {
			// biome-ignore lint/suspicious/noExplicitAny: <explanation>
			const filter: FiltersObject<any> = {};
			const additionalFilters = this.asyncFilters();

			if (this.searchType() === "string" && searchText && searchText.length > 0) {
				// Use searchFields if defined, otherwise fall back to labelKey
				if (async.searchFields && async.searchFields.length > 0) {
					// Create multiple ilike filters for each search field
					const searchFilters = async.searchFields.map((field, index) => ({
						active: true,
						operator: "ilike" as const,
						column: field,
						value: `%${searchText}%`,
					}));

					// Create an OR filter combining all search fields
					filter[`async_search_multiple_fields`] = {
						active: true,
						operator: "or",
						value: searchFilters,
					};
				} else if ("labelKey" in async) {
					// Fallback to original behavior - search only on labelKey
					filter[`async_search_${async.labelKey.toString()}`] = {
						operator: "ilike",
						column: async.labelKey.toString(),
						active: true,
						value: `%${searchText}%`,
					};
				}
			} else if (this.searchType() === "number" && searchNumber !== undefined && searchNumber !== null) {
				filter[`async_search_${async.valueKey.toString()}`] = {
					operator: "eq",
					column: async.valueKey.toString(),
					active: true,
					value: searchNumber,
				};
			}

			if (additionalFilters) {
				for (const k in additionalFilters) {
					filter[k] = additionalFilters[k];
				}
			}

			let loading = true;
			const loadingTimeout$ = timer(200).pipe(
				take(1),
				tap(() => {
					if (loading) {
						this.isLoading.set(true);
					}
				}),
			);

			const dataRequest$ = async.service
				.getMany({
					filters: {
						...(async.filters ?? {}),
						...filter,
					},
					from: 0,
					to: async.limit ?? 200,
				})
				.pipe(
					takeUntil(this.destroy$),
					tap(() => {
						loading = false;
					}),
					tap((items) => {
						this.filteredOptions.set(this.normalizeAsyncOptions(items));
						this.checkDisabledOptions();
						this.isLoading.set(false);
					}),
				);

			loadingTimeout$.pipe(takeUntil(this.destroy$)).subscribe();
			dataRequest$.subscribe();
		} else if (groupedAsync) {
			// biome-ignore lint/suspicious/noExplicitAny: <explanation>
			const filter: any = {};

			if (searchText && searchText.length > 0) {
				// Use searchFields if defined, otherwise fall back to labelKey
				if (groupedAsync.searchFields && groupedAsync.searchFields.length > 0) {
					// Create multiple ilike filters for each search field
					const searchFilters = groupedAsync.searchFields.map(
						(field, index) => ({
							active: true,
							operator: "ilike" as const,
							column: field,
							value: `%${searchText}%`,
						}),
					);

					// Create an OR filter combining all search fields
					filter[`async_search_multiple_fields`] = {
						active: true,
						operator: "or",
						value: searchFilters,
					};
				} else if ("labelKey" in groupedAsync) {
					// Fallback to original behavior - search only on labelKey
					filter[groupedAsync.labelKey] = {
						operator: "ilike",
						column: groupedAsync.labelKey,
						active: true,
						value: `%${searchText}%`,
					};
				}
			}

			let loading = true;
			const loadingTimeout$ = timer(100).pipe(
				take(1),
				tap(() => {
					if (loading) {
						this.isLoading.set(true);
					}
				}),
			);

			const dataRequest$ = groupedAsync.service
				.getMany({
					filters: {
						...(groupedAsync.filters ?? {}),
						...filter,
					},
				})
				.pipe(
					takeUntil(this.destroy$),
					tap(() => {
						loading = false;
					}),
					tap((items) => {
						this.filteredGroupedOptions.set(
							this.normalizeGroupedAsyncOptions(items),
						);
						this.checkDisabledOptions();
						this.isLoading.set(false);
					}),
				);

			loadingTimeout$.pipe(takeUntil(this.destroy$)).subscribe();
			dataRequest$.subscribe();
		} else {
			this.filteredOptions.set(structuredClone(this.options()));
			this.filteredGroupedOptions.set(structuredClone(this.groupedOptions()));

			const filterValue = this.filter();
			if (filterValue && searchText && searchText.length > 0) {
				const fuse = new Fuse(this.filteredOptions(), {
					keys: ["label"],
					threshold: 0.3,
				});

				this.filteredOptions.set(
					fuse.search(searchText).map((result) => result.item),
				);
			}

			if (filterValue && searchText && searchText.length > 0) {
				for (const group of this.filteredGroupedOptions()) {
					const fuse = new Fuse(group.options, {
						keys: ["label"],
						threshold: 0.3,
					});

					group.options = fuse.search(searchText).map((result) => result.item);
				}

				// Filter out empty groups
				this.filteredGroupedOptions.set(
					this.filteredGroupedOptions().filter(
						(group) => group.options.length > 0,
					),
				);
			}

			this.checkDisabledOptions();
		}
	}

	public selectOption(option: CbSelectOption): void {
		if (!option.isDisabled) {
			if (this.innerValue().find((o) => o.value === option.value)) {
				if (this.forceValue()) return;
				this.innerValue.set(
					this.innerValue().filter((o) => o.value !== option.value),
				);
			} else {
				if (this.selectionMode() === "multiple") {
					this.innerValue.set([...this.innerValue(), option]);
				} else {
					this.innerValue.set([option]);
					this.closePopover();
				}
			}

			this.cdr.detectChanges();
		}
	}

	public writeValue(obj: unknown | unknown[]): void {
		setTimeout(() => {
		});
		if (obj !== this.innerValue()) {
			if (Array.isArray(obj)) {
				const newObj = obj.filter((o) => o !== undefined);

				setTimeout(() => {
					if (this.async() || this.groupedAsync()) {
						//this.innerValue.set([]);
						this.loadWriteValueOptions(newObj);
					} else {
						this.values.set(obj);
						this.setInnerValue();
					}
				});
			} else {
				const newObj = obj;
				if (newObj === undefined || newObj === null) {
					this.innerValue.set([]);

					if (this.forceValue()) {
						if (this.filteredOptions().length > 0) {
							this.innerValue.set([this.filteredOptions()[0]]);
						} else {
							this.loadFirstOption().subscribe((res) => {
								if (res) {
									this.innerValue.set([res]);
								}
							});
						}
					}

					return;
				}

				setTimeout(() => {
					if (this.async() || this.groupedAsync()) {
						//this.innerValue.set([]);
						this.loadWriteValueOptions([newObj]);
					} else {
						this.values.set([newObj]);
						this.setInnerValue();
					}
				});
			}
		}
	}

	private setInnerValue(): void {
		this.innerValue.set(
			structuredClone(this.values()).map((o) => {
				return (
					this.currentFlattenedOptions().find(
						(option) => option.value === o,
					) ?? {
						value: o,
						label: "",
					}
				);
			}),
		);
	}

	private loadFirstOption(): Observable<CbSelectOption | null> {
		if (this.isLoadingFirstOption()) return of(null);

		const options = this.filteredOptions();
		if (options && options.length > 0) {
			return of(options[0]);
		}

		const async = this.async();
		const groupedAsync = this.groupedAsync();

		if (async) {
			this.isLoadingFirstOption.set(true);
			return async.service
				.getMany({
					...async.filters,
					from: 0,
					to: 1,
				})
				.pipe(
					tap((res) => {
						const value = res[0][async.valueKey];

						if (value) {
							// Find it in this.options, if it's not there, add it
							const option = this.filteredOptions().find(
								(o) => o.value === value,
							);
							if (!option) {
								this.handleNewOptions([res[0]]);
							}
						}

						this.isLoadingFirstOption.set(false);
					}),
					map((res) => {
						return this.options()[0];
					}),
				);
		}

		if (groupedAsync) {
			this.isLoadingFirstOption.set(true);

			return groupedAsync.service
				.getMany({
					...groupedAsync.filters,
					from: 0,
					to: 1,
				})
				.pipe(
					tap((res) => {
						const value = res[0][groupedAsync.valueKey];

						if (value) {
							// Find it in this.options, if it's not there, add it
							const option = this.filteredOptions().find(
								(o) => o.value === value,
							);
							if (!option) {
								this.handleNewOptions([res[0]]);
							}
						}

						this.isLoadingFirstOption.set(false);
					}),
					map((res) => {
						return this.options()[0];
					}),
				);
		}

		return of(null);
	}

	private loadWriteValueOptions(values: unknown[]): void {
		const async = this.async();
		const groupedAsync = this.groupedAsync();
		if ((!async && !groupedAsync) || !values) return;

		const existingOptions = this.findExistingOptions(values);
		if (existingOptions.length === values.length) {
			this.innerValue.set(existingOptions);
			return;
		}

		const missingValues = this.findMissingValues(values, existingOptions);
		if (missingValues.length === 0) return;

		// Get service and valueKey from async or groupedAsync config
		const service = async ? async.service : groupedAsync?.service;
		const valueKey = async ? async.valueKey : groupedAsync?.valueKey;
		if (!service || !valueKey) return;

		this.isLoading.set(true);
		// Use the batching service instead of direct service call
		this.asyncLoaderService
			.loadValues(service, valueKey, missingValues)
			.pipe(
				takeUntil(this.destroy$),
				tap((items) => {
					const foundOptions = this.handleNewOptions(items, existingOptions);
					// If enableFreeText is true and some values are still missing, create free text options
					if (this.enableFreeText()) {
						const allFoundValues = foundOptions.map((opt) => opt.value);
						const stillMissingValues = missingValues.filter(
							(val) => !allFoundValues.includes(val),
						);

						if (stillMissingValues.length > 0) {
							const freeTextOptions = stillMissingValues.map((value) => ({
								value: value,
								label: String(value),
								item: undefined,
							}));

							const finalOptions = [...foundOptions, ...freeTextOptions];
							this.innerValue.set(finalOptions);
							console.log(`🎯 [SelectComponent] ${this.label()} | Added free text options, total: ${finalOptions.length}`);
						}
					}
				}),
				tap(() => {
					this.isLoading.set(false);
				}),
			)
			.subscribe();
	}

	private findExistingOptions(values: unknown[]): CbSelectOption[] {
		if (this.async()) {
			return this.findExistingAsyncOptions(values);
		}

		if (this.groupedAsync()) {
			return this.findExistingGroupedAsyncOptions(values);
		}

		return [];
	}

	private findExistingAsyncOptions(values: unknown[]): CbSelectOption[] {
		return structuredClone(values)
			.map((value) => this.options().find((option) => option.value === value))
			.filter((option) => !!option) as CbSelectOption[];
	}

	private findExistingGroupedAsyncOptions(values: unknown[]): CbSelectOption[] {
		const allGroupOptions = this.groupedOptions().flatMap(
			(group) => group.options,
		);
		return structuredClone(values)
			.map((value) => allGroupOptions.find((option) => option.value === value))
			.filter((option) => !!option) as CbSelectOption[];
	}

	private findMissingValues(
		values: unknown[],
		existingOptions: CbSelectOption[],
	): unknown[] {
		return values.filter(
			(value) => !existingOptions.find((option) => option.value === value),
		);
	}

	private handleNewOptions(
		data: any[],
		existingOptions: CbSelectOption[] = [],
	): CbSelectOption[] {
		let options = [];
		if (this.async()) {
			options = this.normalizeAsyncOptions(data);
		} else {
			const newOptions = this.normalizeGroupedAsyncOptions(data);
			options = newOptions.flatMap((group) => group.options);
		}

		const allOptions = [...existingOptions, ...options];

		if (this.selectionMode() === "multiple") {
			this.innerValue.set(allOptions);
		} else if (allOptions.length > 0) {
			this.innerValue.set([allOptions[0]]);
		} else {
			this.innerValue.set([]);
		}

		return allOptions;
	}

	private normalizeAsyncOptions(
		items: TablesOrViews<TablesOrViewsKeys>[],
	): CbSelectOption[] {
		return items.map((item) => {
			const async = this.async();
			if (!async) return { label: "", value: "" };

			return {
				item: item,
				value: item[async.valueKey as keyof typeof item],
				label:
					"labelKey" in async
						? item[async.labelKey as keyof typeof item]
						: async.labelCallback(item),
			};
		});
	}

	private normalizeGroupedAsyncOptions(
		items: TablesOrViews<TablesOrViewsKeys>[],
	): CbGroupedSelectOption[] {
		const groupedAsync = this.groupedAsync();
		if (!groupedAsync) return [];
		const groupLabelKey =
			"groupLabelKey" in groupedAsync ? groupedAsync.groupLabelKey : undefined;
		const groupBy = groupedAsync.groupBy;
		const valueKey = groupedAsync.valueKey;
		const labelKey =
			"labelKey" in groupedAsync ? groupedAsync.labelKey : undefined;

		const groupsMap: Record<string, CbGroupedSelectOption> = {};
		for (const item of items) {
			const groupValue = item[groupBy as keyof typeof item];
			const key = String(groupValue);

			// Create group if not exists
			if (!groupsMap[key]) {
				const groupLabel = groupLabelKey
					? (item[groupLabelKey as keyof typeof item] as string)
					: groupedAsync && "groupLabelCallback" in groupedAsync
						? groupedAsync.groupLabelCallback(item)
						: "";
				groupsMap[key] = { label: groupLabel, options: [] };
			}

			const optionLabel = labelKey
				? (item[labelKey as keyof typeof item] as string)
				: groupedAsync && "labelCallback" in groupedAsync
					? groupedAsync.labelCallback(item)
					: "";
			groupsMap[key].options.push({
				item,
				value: item[valueKey as keyof typeof item],
				label: optionLabel,
			});
		}
		return Object.values(groupsMap);
	}

	public onTouchedCallback: () => void = noop;
	// biome-ignore lint/suspicious/noExplicitAny: <explanation>
	public onChangeCallback: (_: any) => void = noop;

	// From ControlValueAccessor interface
	// biome-ignore lint/suspicious/noExplicitAny: <explanation>
	public registerOnChange(fn: any) {
		this.onChangeCallback = fn;
	}

	// From ControlValueAccessor interface
	// biome-ignore lint/suspicious/noExplicitAny: <explanation>
	public registerOnTouched(fn: any) {
		this.onTouchedCallback = fn;
	}

	private isOptionDisabled(option: CbSelectOption): boolean {
		return (
			option.isDisabled ||
			this.disabledOptions().includes(option.value) ||
			(this.disableCheckCallback()?.(option) ?? false)
		);
	}

	public checkDisabledOptions(): void {
		if (this.filteredOptions()?.length > 0) {
			for (const option of this.filteredOptions()) {
				option.isDisabled = this.isOptionDisabled(option);
			}
		}

		if (this.filteredGroupedOptions()?.length > 0) {
			for (const group of this.filteredGroupedOptions()) {
				for (const option of group.options) {
					option.isDisabled = this.isOptionDisabled(option);
				}
			}
		}
	}

	// --- FREE TEXT ---

	protected freeTextInputFocus(event: FocusEvent): void {
		this.popover()?.open();
		this.focusedOptionIndex.set(-1);
	}

	protected freeTextInputBlur(event: FocusEvent): void {
		this.popover()?.close();
		this.focusedOptionIndex.set(-1);
	}

	private normalizeFocusedIndex(): void {
		if (
			this.focusedOptionIndex() >
			this.currentFilteredFlattenedOptions().length - 1
		) {
			this.focusedOptionIndex.set(
				this.currentFilteredFlattenedOptions().length - 1,
			);
		}
	}

	protected freeTextInput(event: Event): void {
		this.normalizeFocusedIndex();
	}

	protected freeTextInputEnter(event: Event): void {
		event.preventDefault();
		event.stopImmediatePropagation();

		if (
			this.focusedOptionIndex() >= 0 &&
			this.currentFilteredFlattenedOptions().length > this.focusedOptionIndex()
		) {
			this.selectOption(
				this.currentFilteredFlattenedOptions()[this.focusedOptionIndex()],
			);
			this.searchText.set("");
			this.applyFilters$.next();
			this.normalizeFocusedIndex();
		} else if (this.focusedOptionIndex() === -1 && this.showFreeTextInsert()) {
			this.addFreeText();
		}
	}

	protected freeTextInputDown(event: Event): void {
		event.preventDefault();
		event.stopImmediatePropagation();

		if (
			this.focusedOptionIndex() <
			this.currentFilteredFlattenedOptions().length - 1
		) {
			this.focusedOptionIndex.set(this.focusedOptionIndex() + 1);
		}
	}

	protected freeTextInputUp(event: Event): void {
		event.preventDefault();
		event.stopImmediatePropagation();

		if (this.focusedOptionIndex() > 0) {
			this.focusedOptionIndex.set(this.focusedOptionIndex() - 1);
		}
	}

	protected addFreeText(): void {
		this.selectOption({
			value: this.searchText() ?? "",
			label: this.searchText() ?? "",
		});
		this.searchText.set("");
		this.focusedOptionIndex.set(-1);
		this.applyFilters$.next();
	}

	protected freeTextInputDelete(event: Event, index?: number): void {
		if (this.searchText() === "" || this.searchText() === null) {
			event.stopImmediatePropagation();

			// Remove the last selected option
			this.removeValueAtIndex(index ?? this.innerValue().length - 1);
		}
	}

	public removeValueAtIndex(index: number): void {
		this.innerValue.set(this.innerValue().filter((_, i) => i !== index));
	}

	// -----------------

	// --- KEYBOARD NAVIGATION ---

	public onArrowDown(event: Event): void {
		event.preventDefault();
		event.stopImmediatePropagation();

		if (!this.isPopoverOpened()) {
			this.popover()?.open();
			return;
		}

		const maxIndex = this.currentFilteredFlattenedOptions().length - 1;
		if (this.focusedOptionIndex() < maxIndex) {
			this.focusedOptionIndex.set(this.focusedOptionIndex() + 1);
		} else {
			// Wrap to first option
			this.focusedOptionIndex.set(0);
		}

		this.scrollToFocusedOption();
	}

	public onArrowUp(event: Event): void {
		event.preventDefault();
		event.stopImmediatePropagation();

		if (!this.isPopoverOpened()) {
			this.popover()?.open();
			return;
		}

		if (this.focusedOptionIndex() > 0) {
			this.focusedOptionIndex.set(this.focusedOptionIndex() - 1);
		} else {
			// Wrap to last option
			const maxIndex = this.currentFilteredFlattenedOptions().length - 1;
			this.focusedOptionIndex.set(maxIndex);
		}

		this.scrollToFocusedOption();
	}

	public onEnter(event: Event): void {
		event.preventDefault();
		event.stopImmediatePropagation();

		if (!this.isPopoverOpened()) {
			this.popover()?.open();
			return;
		}

		const focusedIndex = this.focusedOptionIndex();
		const options = this.currentFilteredFlattenedOptions();

		if (focusedIndex >= 0 && focusedIndex < options.length) {
			const focusedOption = options[focusedIndex];
			if (!focusedOption.isDisabled) {
				this.selectOption(focusedOption);
			}
		}
	}

	public onEscape(event: Event): void {
		event.preventDefault();
		event.stopImmediatePropagation();

		if (this.isPopoverOpened()) {
			this.popover()?.close();
		}
	}

	public getGlobalOptionIndex(groupIndex: number, optionIndex: number): number {
		let globalIndex = 0;
		const groups = this.filteredGroupedOptions();

		for (let i = 0; i < groupIndex; i++) {
			globalIndex += groups[i].options.length;
		}

		return globalIndex + optionIndex;
	}

	private scrollToFocusedOption(): void {
		// Wait for DOM to update
		requestAnimationFrame(() => {
			const focusedIndex = this.focusedOptionIndex();
			const dropdown = this.dropdownContainer();

			console.log(
				"Scroll to focused option:",
				focusedIndex,
				"dropdown:",
				!!dropdown,
			);

			if (!dropdown || focusedIndex < 0) return;

			// Check if virtual scroll is enabled
			if (this.virtualScrollEnabled()) {
				// For virtual scroll, use the viewport reference
				const viewport = this.virtualScrollViewport();
				console.log("Virtual scroll viewport:", !!viewport);
				if (viewport) {
					console.log("Scrolling to index:", focusedIndex);
					viewport.scrollToIndex(focusedIndex, "smooth");
				}
			} else {
				// For normal scroll, manually scroll the option into view
				const optionElement = dropdown.nativeElement.querySelector(
					`#cb-select-option-${focusedIndex}`,
				) as HTMLElement;

				console.log(
					"Option element found:",
					!!optionElement,
					`#cb-select-option-${focusedIndex}`,
				);

				if (optionElement) {
					// Find the scrollable container - could be the dropdown itself or a nested container
					const scrollContainer = this.findScrollableContainer(
						dropdown.nativeElement,
					);
					console.log("Scroll container:", scrollContainer);

					if (scrollContainer) {
						const containerTop = scrollContainer.scrollTop;
						const containerHeight = scrollContainer.clientHeight;
						const optionTop = optionElement.offsetTop;
						const optionHeight = optionElement.offsetHeight;

						console.log("Scroll info:", {
							containerTop,
							containerHeight,
							optionTop,
							optionHeight,
						});

						// Check if option is above visible area
						if (optionTop < containerTop) {
							console.log("Scrolling up to:", optionTop);
							scrollContainer.scrollTop = optionTop;
						}
						// Check if option is below visible area
						else if (
							optionTop + optionHeight >
							containerTop + containerHeight
						) {
							const newScrollTop = optionTop + optionHeight - containerHeight;
							console.log("Scrolling down to:", newScrollTop);
							scrollContainer.scrollTop = newScrollTop;
						}
					}
				}
			}
		});
	}

	private findScrollableContainer(element: HTMLElement): HTMLElement | null {
		// First check if the element itself is scrollable
		const style = window.getComputedStyle(element);
		if (
			style.overflowY === "auto" ||
			style.overflowY === "scroll" ||
			element.scrollHeight > element.clientHeight
		) {
			return element;
		}

		// Look for a scrollable child container (like .cb-select-dropdown-options)
		const candidates = [".cb-select-dropdown-options", ".cb-virtual-scroll"];

		for (const selector of candidates) {
			const container = element.querySelector(selector) as HTMLElement;
			if (container) {
				const containerStyle = window.getComputedStyle(container);
				if (
					containerStyle.overflowY === "auto" ||
					containerStyle.overflowY === "scroll" ||
					container.scrollHeight > container.clientHeight
				) {
					return container;
				}
			}
		}

		return element; // fallback to the original element
	}

	// -----------------

	public popoverOpened(): void {
		this.isPopoverOpened.set(true);
		this.focusedOptionIndex.set(0); // Reset focus to first option when opening
		this.applyFilters$.next();
	}

	public popoverClosed(): void {
		this.isPopoverOpened.set(false);
	}

	public reset(): void {
		this.innerValue.set([]);
	}

	public closePopover(): void {
		this.popover()?.close();
	}

	ngOnDestroy() {
		this.destroy$.next();
		this.destroy$.complete();
	}
}
